import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BaseComponent } from '../../../shared/base-classes/base.component';
import {
  DateUtil,
  HeroEventResponseDto,
  ScheduledHeroEventResponseDto,
  ScheduledHeroEventStudentListItemDto,
  ScheduledHeroEventUpdateDto,
  StudentBehaviorHistoryResponseDto,
} from '@whetstoneeducation/hero-common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormControl } from '@angular/forms';
import { AppToastManagerService } from '../../../shared/services/toast-manager.service';
import { AppPageHeaderService } from '../../../shared/page-header/page-header.service';
import { AppEventService } from '../events.service';
import { MatDialog } from '@angular/material/dialog';
import { AppAddStudentToScheduledHeroEventModalComponent } from './add-student-to-scheduled-hero-event-modal/add-student-to-scheduled-hero-event-modal.component';
import { scan, Subscription, takeUntil } from 'rxjs';
import { HeaderButtonAction } from '../../../shared/page-header/header-button';
import { DEFAULT_DIALOG_RESULT } from '../../../shared/confirmation-dialog/confirmation-dialog-options.interface';
import { AppConfirmationDialogService } from '../../../shared/confirmation-dialog/confirmation-dialog.service';
import { ScheduledHeroEventScanStudentDialogComponent } from './scheduled-hero-event-scan-student-dialog/scheduled-hero-event-scan-student-dialog.component';
import { ScheduledHeroEventOverdueActionsDialogComponent } from './scheduled-hero-event-overdue-actions-dialog/scheduled-hero-event-overdue-actions-dialog.component';
import { AppStudentsService } from '../../students/students.service';
import { IStudentBehaviorHistoryFilter } from '../../students/students-list-models/student-behavior-history-filters';

@Component({
  selector: 'app-scheduled-hero-event-view-edit',
  templateUrl: './scheduled-hero-event-view-edit.component.html',
  styleUrls: ['./scheduled-hero-event-view-edit.component.scss']
})
export class ScheduledHeroEventViewEditComponent
  extends BaseComponent
  implements OnInit, OnDestroy {
  public scheduledHeroEvent: ScheduledHeroEventResponseDto;
  public heroEvent: HeroEventResponseDto;
  public scheduledHeroEventStudentListItems: ScheduledHeroEventStudentListItemDto[];
  public filteredEventStudents: ScheduledHeroEventStudentListItemDto[];
  public pageHeaderSubscription: Subscription;
  public searchControl = new FormControl('');
  public subscriptions: Subscription[] = [];
  public reportView: boolean = false;
  @ViewChild('scanButton', { read: ElementRef }) scanButton: ElementRef;
  scannedBarcode = "";
  lastKeyPressTime = 0;
  SCAN_THRESHOLD_MS = 100;
  public showToastMessage = true;
  public eventStarted = false;
  constructor(
    public route: ActivatedRoute,
    public formBuilder: FormBuilder,
    public toastManager: AppToastManagerService,
    public router: Router,
    private pageHeaderService: AppPageHeaderService,
    public eventsService: AppEventService,
    public dialog: MatDialog,
    public scanDialog: MatDialog,
    public overdueDialog: MatDialog,
    public confirmationDialog: AppConfirmationDialogService,
    private readonly toastService: AppToastManagerService,
    public studentsService: AppStudentsService,
  ) {
    super();
  }


  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    const currentTime = new Date().getTime();
    if (currentTime - this.lastKeyPressTime > this.SCAN_THRESHOLD_MS) {
      this.scannedBarcode = "";
    }

    if (event.key === "Enter") {
      if (this.scannedBarcode) {
        this.updateStudentFromDialog(this.scannedBarcode);
      }

      this.scannedBarcode = "";
    } else if (event.key.length === 1 && this.scanDialog.openDialogs.length === 0) {
      this.scannedBarcode += event.key;
    }

    this.lastKeyPressTime = currentTime;
  }

  ngOnInit() {
    this.loadData(this.route.snapshot.data.data);
    this.subscriptions.push(
      this.searchControl.valueChanges.subscribe((searchValue) => {
        this.filteredEventStudents =
          this.scheduledHeroEventStudentListItems.filter((student) => {
            return student.name
              .toLowerCase()
              .includes(searchValue.toLowerCase());
          });
      })
    );
    if (this.scheduledHeroEvent.startTime && this.scheduledHeroEvent.endTime) {

      this.subscriptions.push(
        this.pageHeaderService.buttonAction$.subscribe(
          async (action: string) => {
            if (action === HeaderButtonAction.EXPORT) {
              const ref = this.confirmationDialog.openDialog({
                title: `Event Export`,
                content: 'Are you sure you want to export the event report?'
              });
              ref.afterClosed().subscribe(async (result: string) => {
                if (result === DEFAULT_DIALOG_RESULT.YES) {
                  await this.eventsService.getScheduledHeroEventExport(
                    this.scheduledHeroEvent.id
                  );
                  this.toastService.success(
                    'Export started, we will email you with a link to the file'
                  );
                }
              });
            }
          }
        )
      );
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public async refreshData() {
    this.isLoading = true;
    this.loadData(
      await this.eventsService.getScheduledHeroEvent(this.scheduledHeroEvent.id)
    );
    this.isLoading = false;
  }

  public loadData(scheduledHeroEvent: ScheduledHeroEventResponseDto): void {
    this.scheduledHeroEvent = scheduledHeroEvent;
    this.heroEvent = scheduledHeroEvent.heroEvent;

    this.pageHeaderService.changeTitle(
      `Event: ${this.scheduledHeroEvent.heroEvent.name}`
    );

    this.pageHeaderService.changeButtons(
      scheduledHeroEvent.startTime && scheduledHeroEvent.endTime
        ? [
            {
              name: 'Export',
              action: HeaderButtonAction.EXPORT,
              style: 'secondary-button'
            }
          ]
        : []
    );

    this.scheduledHeroEventStudentListItems =
      this.scheduledHeroEvent.studentScheduledHeroEvents;
    this.filteredEventStudents = this.scheduledHeroEventStudentListItems;

    this.reportView = !!(
      DateUtil.isTodayOrPast(this.scheduledHeroEvent.calendarDay.date) &&
      this.scheduledHeroEvent.endTime
    );

    this.eventStarted = this.scheduledHeroEvent.startTime && !this.scheduledHeroEvent.endTime;
  }

  public showAddStudentModal(): void {
    this.dialog
      .open(AppAddStudentToScheduledHeroEventModalComponent, {
        width: '800px',
        height: '600px',
        panelClass: 'default-dialog',
        data: {
          scheduledHeroEventId: this.scheduledHeroEvent.id,
          onClose: async () => { }
        }
      })
      .afterClosed()
      .subscribe(async () => {
        this.isLoading = true;
        const heroScheduledEvent =
          await this.eventsService.getScheduledHeroEvent(
            this.scheduledHeroEvent.id
          );
        this.loadData(heroScheduledEvent);
        this.isLoading = false;
      });
  }

  public async updateStudent(student: ScheduledHeroEventStudentListItemDto) {
    const filters = {
      overdueActions: true,
    } as IStudentBehaviorHistoryFilter;

    const reactionHistory = await this.studentsService.getStudentBehaviorHistory(student.studentId, filters);
    const studentHasOverdueActions = reactionHistory.results.length > 0;
    const chekingInFirstTime = student.checkInTimes?.length === 0;

    if (studentHasOverdueActions && chekingInFirstTime) {
      this.showOverdueActionDialog(student, reactionHistory.results);
      return;
    }

    await this.eventsService.checkInOutEventStudent(student, this.scheduledHeroEvent.id);
    await this.refreshData();
  }

  async updateStudentFromDialog(studentExtId: string): Promise<void> {
    this.eventStarted = this.scheduledHeroEvent.startTime && !this.scheduledHeroEvent.endTime;
    if (!this.eventStarted) {
      return;
    }
    const student = this.scheduledHeroEventStudentListItems.find(student => student.studentExtId === studentExtId);
    if (!student) {
      this.toastService.error('This student is not on the invite list for this event.')
      return;
    }

    if (
      student.checkInTimes.length > 0 &&
      student.checkOutTimes.length > 0 &&
      !this.scheduledHeroEvent.heroEvent.allowMultipleEntries
    ) {
      this.toastService.error('This event does not allow multiple entries.')
      return;
    }

    this.updateStudent(student);

    const chekingIn = student.checkInTimes.length === student.checkOutTimes.length;
    this.toastService.success(`${student.name} has been checked ${chekingIn ? 'in' : 'out'}`);
  }

  public showScanStudentModal(): void {
    this.scanDialog
      .open(ScheduledHeroEventScanStudentDialogComponent, {
        width: '800px',
        height: '350px',
        panelClass: 'default-dialog',
        data: {
          scheduledHeroEventId: this.scheduledHeroEvent.id,
          onClose: async () => { },
          updateStudent: this.updateStudentFromDialog.bind(this)
        }
      })
      .afterClosed()
      .subscribe(async () => {
        this.isLoading = true;
        const heroScheduledEvent =
          await this.eventsService.getScheduledHeroEvent(
            this.scheduledHeroEvent.id
          );
        this.loadData(heroScheduledEvent);
        this.isLoading = false;
        this.scanButton.nativeElement.blur();
      });
  }

  public showOverdueActionDialog(student: ScheduledHeroEventStudentListItemDto, reactions: StudentBehaviorHistoryResponseDto[]): void {
    this.overdueDialog
      .open(ScheduledHeroEventOverdueActionsDialogComponent, {
        width: '800px',
        height: 'auto',
        panelClass: 'default-dialog',
        data: {
          student,
          reactions,
          eventId: this.scheduledHeroEvent.id,
          onClose: async () => { }
        }
      })
      .afterClosed()
      .subscribe(async () => {
        this.isLoading = true;
        const heroScheduledEvent =
          await this.eventsService.getScheduledHeroEvent(
            this.scheduledHeroEvent.id
          );
        this.loadData(heroScheduledEvent);
        this.isLoading = false;
      });
  }

  getCalendarDayDate() {
    return DateUtil.convertToDisplayDate(
      new Date(this.scheduledHeroEvent.calendarDay.date).getTime()
    );
  }

  async startEvent() {
    this.isLoading = true;
    const updatedScheduledHeroEvent =
      await this.eventsService.updateScheduledHeroEvent(
        this.scheduledHeroEvent.id,
        new ScheduledHeroEventUpdateDto({
          start: true
        })
      );
    this.loadData(updatedScheduledHeroEvent);
    this.isLoading = false;
  }

  async endEvent() {
    this.isLoading = true;
    const updatedScheduledHeroEvent =
      await this.eventsService.updateScheduledHeroEvent(
        this.scheduledHeroEvent.id,
        new ScheduledHeroEventUpdateDto({
          end: true
        })
      );
    this.loadData(updatedScheduledHeroEvent);
    this.isLoading = false;
  }

  onCloseToast() {
    this.showToastMessage = !this.showToastMessage;
  }

  protected readonly DateUtil = DateUtil;
  protected readonly Date = Date;
}
